import { useEffect, useState, useRef } from 'react';
import { 
  Briefcase, 
  Brain, 
  Code, 
  Zap, 
  DollarSign, 
  Settings 
} from 'lucide-react';

const solutions = [
  {
    number: '01',
    icon: Briefcase,
    title: 'HireSphere Recruitment Platform',
    description: 'AI-enabled, credit-based recruitment web application with video-based assessments and collaborative hiring tools.',
  },
  {
    number: '02',
    icon: Brain,
    title: 'RavenRecruit — AI Recruitment Intelligence',
    description: 'AI-native "Business Memory" platform unifying CFO, COO, and HR intelligence with real-time analytics.',
  },
  {
    number: '03',
    icon: Code,
    title: 'Custom Software Development',
    description: 'End-to-end bespoke application development for web, mobile, and enterprise systems tailored to your needs.',
  },
  {
    number: '04',
    icon: Zap,
    title: 'Digital Transformation & Automation',
    description: 'Designing and implementing phased automation roadmaps and system integration solutions.',
  },
  {
    number: '05',
    icon: DollarSign,
    title: 'Cloud-Based Financial Systems',
    description: 'Scalable cloud-based financial management and lending platforms built on Azure/AWS.',
  },
  {
    number: '06',
    icon: Settings,
    title: 'IT Consulting & System Architecture',
    description: 'Strategic advisory on IT infrastructure, system architecture, ERP integrations, and technology roadmaps.',
  },
];

export default function SolutionsSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="solutions"
      ref={sectionRef}
      data-testid="solutions-section"
      className="py-24 sm:py-32 bg-[#0D1321] relative overflow-hidden"
    >
      {/* Circuit Board Texture Overlay */}
      <img
        src="https://static.prod-images.emergentagent.com/jobs/651541d7-5e6b-42ae-bbbb-a15776d2b14a/images/e4999b22e6e96b63f63379e586d4e089214cb10290beac1b50287d8c22ef2507.png"
        alt=""
        loading="lazy"
        decoding="async"
        className="circuit-overlay"
        aria-hidden="true"
      />

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-[#F5A623] text-xs tracking-[0.2em] uppercase font-semibold mb-4">
            Solutions & Services
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-bold text-white">
            What We Build
          </h2>
        </div>

        {/* Solutions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {solutions.map((solution, index) => (
            <div
              key={solution.number}
              data-testid={`solution-card-${index}`}
              className={`glass-card rounded-2xl p-8 sm:p-10 reveal ${
                isVisible ? 'visible' : ''
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="flex items-start justify-between mb-6">
                <solution.icon className="w-10 h-10 text-[#F5A623]" />
                <span className="text-white/20 text-sm font-bold">{solution.number}</span>
              </div>
              <h3 className="text-xl sm:text-2xl tracking-tight font-semibold text-white mb-4">
                {solution.title}
              </h3>
              <p className="text-base leading-relaxed text-white/70">
                {solution.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
